import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum, getCrudStatusLabel, localStorageKeys, permissionLevelEnum } from '../../../../constants';
import { getCrudStatusColor } from '../../../../constants/colors';
import { useUpdateTempWorkerMutation } from '../../../../hooks/mutations/useTempWorkerMutation';
import { useTempWorkersQuery } from '../../../../hooks/queries/useTempWorkersQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { TempWorker } from '../../../../services/apiSLTP/materials/tempWorkers';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import { createDateTimeColumn } from '../../../../utils/tables/commonColumns';
import StateCell from '../../../cells/StateCell';
import UpdateTempWorkerModal from '../../../modals/admin/external/UpdateTempWorkerModal';
import DeleteModal from '../../../modals/DeleteModal';
import IconButton from '../../../ui/IconButton';
import IconLine from '../../../ui/IconLine';
import GenericAgGridTable from '../../GenericAgGridTable';

interface TempWorkersSettingsRowProps {
    withUsageCount: boolean;
}

const TempWorkersSettingsRow: React.FC<TempWorkersSettingsRowProps> = ({ withUsageCount }) => {
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTempWorker, setSelectedTempWorker] = useState<TempWorker | null>(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const { t } = useTranslation();
    const { user } = useAuth();

    const {
        data: tempWorkersData,
        isLoading,
        refetch,
    } = useTempWorkersQuery({
        queries: { withUsage: withUsageCount },
    });

    const { mutate: mutateTempWorker } = useUpdateTempWorkerMutation({
        onSuccess: () => refetch(),
        onError: (error) => console.error('Error updating tempWorker:', error.message),
    });

    const columnDefs: ColDef<TempWorker>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeProC',
            initialWidth: 150,
        },
        {
            headerName: capitalizeFirstLetter(t('actions')),
            cellRenderer: (params: ICellRendererParams<TempWorker>) => {
                if (!params.data) return null;
                return (
                    <IconLine>
                        <IconButton
                            iconName="edit"
                            title={capitalizeFirstLetter(t('modifyTempWorker'))}
                            onClick={() => {
                                if (!params.data) return;
                                setSelectedTempWorker(params.data);
                                setIsEditMode(true);
                                setShowEditModal(true);
                            }}
                            disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                        />
                        {user && user?.profileId.permsLevel <= permissionLevelEnum.ADMIN && (
                            <IconButton
                                iconName="delete"
                                title={capitalizeFirstLetter(t('deleteTempWorker'))}
                                onClick={() => {
                                    if (!params.data) return;
                                    setSelectedTempWorker(params.data);
                                    setShowDeleteModal(true);
                                }}
                                disabled={params.data.crudStatus === crudStatusEnum.DELETED}
                            />
                        )}
                    </IconLine>
                );
            },
            colId: 'actions',
            filter: false,
            enableRowGroup: false,
            enableValue: false,
            sortable: false,
        },
        {
            headerName: capitalizeFirstLetter(t('status')),
            valueGetter: (params) => capitalizeFirstLetter(t(getCrudStatusLabel(params.data?.crudStatus))),
            cellRenderer: (params: ICellRendererParams<TempWorker>) => {
                if (!params.data) return params.value;
                return <StateCell stateColor={getCrudStatusColor(params.data?.crudStatus)} text={t(getCrudStatusLabel(params.data?.crudStatus))} />;
            },
            colId: 'status',
        },
        {
            headerName: capitalizeFirstLetter(t('email')),
            field: 'email',
            colId: 'email',
        },
        createDateTimeColumn<TempWorker>('createdAt', 'createdAt'),
        createDateTimeColumn<TempWorker>('updatedAt', 'updatedAt'),
    ];

    if (user?.profileId.permsLevel === permissionLevelEnum.ADMIN) {
        columnDefs.push(
            {
                headerName: capitalizeFirstLetter(t('_id')),
                field: '_id',
                initialWidth: 250,
                colId: '_id',
                cellClass: 'usage-col-main',
                headerClass: 'usage-col-main',
            },
            {
                headerName: capitalizeFirstLetter(t('xTotal')),
                field: 'usageCount.total',
                colId: 'xtotal',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            },
            {
                headerName: capitalizeFirstLetter(t('xTempWorkers')),
                field: 'usageCount.dailyTempWorkers',
                colId: 'xtempworkers',
                cellClass: 'usage-col',
                headerClass: 'usage-col',
                filter: 'agNumberColumnFilter',
            }
        );
    }

    const sortedTempWorkers = useMemo(() => {
        return tempWorkersData?.sort((a, b) => a.designation.localeCompare(b.designation)) || [];
    }, [tempWorkersData]);

    return (
        <>
            <GenericAgGridTable
                rowData={sortedTempWorkers}
                columnDefs={columnDefs}
                loading={isLoading}
                exportCsvFileName="admin-tempworkers"
                headerRightComponent={
                    <IconButton
                        iconName="addCircle"
                        onClick={() => {
                            setSelectedTempWorker(null);
                            setIsEditMode(false);
                            setShowEditModal(true);
                        }}
                        title={capitalizeFirstLetter(t('addTempWorker'))}
                    />
                }
                storageKey={localStorageKeys.TABLE.ADMIN.EXTERNAL.TEMPWORKERS}
                agGridProps={{ getRowId: ({ data }) => data._id }}
            />

            {showEditModal && (
                <UpdateTempWorkerModal
                    showEditModal={showEditModal}
                    setShowEditModal={setShowEditModal}
                    isEditMode={isEditMode}
                    tempWorker={selectedTempWorker}
                    refetch={refetch}
                />
            )}

            {showDeleteModal && selectedTempWorker && (
                <DeleteModal
                    open={showDeleteModal}
                    handleClose={() => setShowDeleteModal(false)}
                    deleteAction={() => {
                        mutateTempWorker({
                            tempWorkerId: selectedTempWorker._id,
                            data: { crudStatus: crudStatusEnum.DELETED },
                        });
                    }}
                    title={t('tempWorker')}
                    itemName={selectedTempWorker.designation}
                />
            )}
        </>
    );
};

export default TempWorkersSettingsRow;
